import { supabase } from "@/integrations/supabase/client";

export interface SavedJobDescription {
  id: string;
  companyName: string;
  jobTitle: string;
  companyMission: string;
  selectedResponsibilities: string[];
  selectedRequirements: string[];
  selectedSkills: string[];
  allResponsibilities: string[];
  allRequirements: string[];
  allSkills: string[];
  matchScore: number;
  dateCreated: string;
}

export interface CreateJobDescriptionData {
  companyName: string;
  jobTitle: string;
  companyMission: string;
  selectedResponsibilities: string[];
  selectedRequirements: string[];
  selectedSkills: string[];
  allResponsibilities: string[];
  allRequirements: string[];
  allSkills: string[];
  matchScore: number;
}

export class JobLibraryService {
  /**
   * Get all saved job descriptions for the current user
   */
  static async getSavedJobs(): Promise<SavedJobDescription[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('saved_job_descriptions')
      .select('*')
      .eq('user_id', user.id) // CRITICAL: Filter by current user only
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching saved jobs:', error);
      throw new Error('Failed to fetch saved job descriptions');
    }

    return data.map(job => ({
      id: job.id,
      companyName: job.company_name,
      jobTitle: job.job_title,
      companyMission: job.company_mission || '',
      selectedResponsibilities: (job.selected_responsibilities as string[]) || [],
      selectedRequirements: (job.selected_requirements as string[]) || [],
      selectedSkills: (job.selected_skills as string[]) || [],
      allResponsibilities: (job.all_responsibilities as string[]) || [],
      allRequirements: (job.all_requirements as string[]) || [],
      allSkills: (job.all_skills as string[]) || [],
      matchScore: job.match_score || 0,
      dateCreated: job.created_at
    }));
  }

  /**
   * Save a new job description
   */
  static async saveJob(jobData: CreateJobDescriptionData): Promise<SavedJobDescription> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      throw new Error('User must be authenticated to save job descriptions');
    }

    const { data, error } = await supabase
      .from('saved_job_descriptions')
      .insert({
        user_id: session.user.id,
        company_name: jobData.companyName,
        job_title: jobData.jobTitle,
        company_mission: jobData.companyMission,
        selected_responsibilities: jobData.selectedResponsibilities,
        selected_requirements: jobData.selectedRequirements,
        selected_skills: jobData.selectedSkills,
        all_responsibilities: jobData.allResponsibilities,
        all_requirements: jobData.allRequirements,
        all_skills: jobData.allSkills,
        match_score: jobData.matchScore
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving job:', error);
      throw new Error('Failed to save job description');
    }

    return {
      id: data.id,
      companyName: data.company_name,
      jobTitle: data.job_title,
      companyMission: data.company_mission || '',
      selectedResponsibilities: (data.selected_responsibilities as string[]) || [],
      selectedRequirements: (data.selected_requirements as string[]) || [],
      selectedSkills: (data.selected_skills as string[]) || [],
      allResponsibilities: (data.all_responsibilities as string[]) || [],
      allRequirements: (data.all_requirements as string[]) || [],
      allSkills: (data.all_skills as string[]) || [],
      matchScore: data.match_score || 0,
      dateCreated: data.created_at
    };
  }

  /**
   * Delete a saved job description
   */
  static async deleteJob(jobId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('saved_job_descriptions')
      .delete()
      .eq('id', jobId)
      .eq('user_id', user.id); // CRITICAL: Only allow deletion of user's own jobs

    if (error) {
      console.error('Error deleting job:', error);
      throw new Error('Failed to delete job description');
    }
  }

  /**
   * Update a saved job description
   */
  static async updateJob(jobId: string, updates: Partial<CreateJobDescriptionData>): Promise<SavedJobDescription> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const updateData: any = {};
    
    if (updates.companyName) updateData.company_name = updates.companyName;
    if (updates.jobTitle) updateData.job_title = updates.jobTitle;
    if (updates.companyMission !== undefined) updateData.company_mission = updates.companyMission;
    if (updates.selectedResponsibilities) updateData.selected_responsibilities = updates.selectedResponsibilities;
    if (updates.selectedRequirements) updateData.selected_requirements = updates.selectedRequirements;
    if (updates.selectedSkills) updateData.selected_skills = updates.selectedSkills;
    if (updates.allResponsibilities) updateData.all_responsibilities = updates.allResponsibilities;
    if (updates.allRequirements) updateData.all_requirements = updates.allRequirements;
    if (updates.allSkills) updateData.all_skills = updates.allSkills;
    if (updates.matchScore !== undefined) updateData.match_score = updates.matchScore;

    const { data, error } = await supabase
      .from('saved_job_descriptions')
      .update(updateData)
      .eq('id', jobId)
      .eq('user_id', user.id) // CRITICAL: Only allow updates to user's own jobs
      .select()
      .single();

    if (error) {
      console.error('Error updating job:', error);
      throw new Error('Failed to update job description');
    }

    return {
      id: data.id,
      companyName: data.company_name,
      jobTitle: data.job_title,
      companyMission: data.company_mission || '',
      selectedResponsibilities: (data.selected_responsibilities as string[]) || [],
      selectedRequirements: (data.selected_requirements as string[]) || [],
      selectedSkills: (data.selected_skills as string[]) || [],
      allResponsibilities: (data.all_responsibilities as string[]) || [],
      allRequirements: (data.all_requirements as string[]) || [],
      allSkills: (data.all_skills as string[]) || [],
      matchScore: data.match_score || 0,
      dateCreated: data.created_at
    };
  }
}